import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowUpRight } from "lucide-react";

const projects = [
  {
    name: "Cocoon Mind",
    industry: "Plateforme d'apprentissage IA · SaaS",
    year: "2026",
    tag: "Plateforme web · IA & Communauté",
    image: "/cocoon-screenshot.png",
    href: "https://www.cocoonmind.com",
    quote:
      "Un espace où la curiosité redevient le seul moteur — sans école, sans notes.",
  },
  {
    name: "Chango's Renovation",
    industry: "Construction & Rénovation · Bruxelles",
    year: "2025",
    tag: "Site vitrine + Identité",
    image: "/changos-screenshot.png",
    href: "https://changos-renovation.com/",
    quote:
      "Une présence en ligne qui inspire enfin confiance, comme notre travail.",
  },
  {
    name: "Teralya",
    industry: "Aventures Nature & Bien-être · Belgique",
    year: "2025",
    tag: "Site vitrine + Réservations",
    image: "/teralya-screenshot.png",
    href: "https://teralya.be/",
    quote:
      "Notre communauté a enfin un site à la hauteur de nos aventures.",
  },
  {
    name: "El Camino",
    industry: "Randonnée Costa Blanca · Alicante",
    year: "2025",
    tag: "Plateforme web · Multilingue",
    image: "/camino-screenshot.png",
    href: "https://camino-staging-1.preview.emergentagent.com",
    quote:
      "Chaque sentier, chaque pèlerin, tout est enfin réuni au même endroit.",
  },
  {
    name: "PromptyMarket",
    industry: "Marketplace de prompts IA · SaaS",
    year: "2025",
    tag: "Plateforme web · Identité",
    image: "/prompty-screenshot.png",
    href: "https://prompty-redesign-1.preview.emergentagent.com",
    quote:
      "Une plateforme qui assume enfin notre positionnement premium et notre rigueur.",
  },
];

export default function Portfolio() {
  const scrollerRef = useRef(null);
  const pausedRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Duplicate projects for seamless infinite scroll
  const loopProjects = [...projects, ...projects];

  // Slow auto-scroll loop with seamless reset
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduce) return;

    let rafId;
    let last = performance.now();
    let accum = 0; // fractional accumulator (browsers round scrollLeft to int)
    const SPEED = 22; // px/sec — very slow drift

    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!pausedRef.current && el.scrollWidth > el.clientWidth) {
        accum += SPEED * dt;
        if (accum >= 1) {
          const inc = Math.floor(accum);
          accum -= inc;
          el.scrollLeft += inc;
          const half = el.scrollWidth / 2;
          if (el.scrollLeft >= half) el.scrollLeft -= half;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
  }, []);
  const resume = useCallback(() => {
    pausedRef.current = false;
  }, []);

  // Track which project is currently centered — powers the dot indicators
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let ticking = false;
    const updateActive = () => {
      const cards = el.querySelectorAll("[data-portfolio-card]");
      if (!cards.length) return;
      const center = el.scrollLeft + el.clientWidth / 2;
      let nearest = 0;
      let minDist = Infinity;
      cards.forEach((card, idx) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(cardCenter - center);
        if (dist < minDist) {
          minDist = dist;
          nearest = idx;
        }
      });
      setActiveIndex(nearest % projects.length);
    };
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActive();
          ticking = false;
        });
        ticking = true;
      }
    };
    updateActive();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActive);
    };
  }, []);

  const scrollToIndex = useCallback((idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (!cards.length) return;

    // Find the copy of the target card that's closest to current viewport
    // (loopProjects duplicates the array, so we have 2× cards for each idx)
    const currentCenter = el.scrollLeft + el.clientWidth / 2;
    let bestCard = null;
    let bestDist = Infinity;
    cards.forEach((card, i) => {
      if (i % projects.length !== idx) return;
      const cardRect = card.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const cardLeftInContent =
        cardRect.left - elRect.left + el.scrollLeft;
      const cardCenter = cardLeftInContent + cardRect.width / 2;
      const dist = Math.abs(cardCenter - currentCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestCard = card;
      }
    });
    if (!bestCard) return;

    const cardRect = bestCard.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const cardLeftInContent =
      cardRect.left - elRect.left + el.scrollLeft;
    const target =
      cardLeftInContent - (el.clientWidth - cardRect.width) / 2;

    // Pause auto-scroll while the smooth-scroll animation runs
    pausedRef.current = true;
    el.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
    window.setTimeout(() => {
      pausedRef.current = false;
    }, 1200);
  }, []);

  return (
    <section
      id="realisations"
      data-testid="portfolio-section"
      className="py-20 md:py-28 bg-[#F3F0FF] overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 mb-12 md:mb-16">
        <div>
          <span className="eyebrow">— Réalisations</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-[#0B1027] mt-5 leading-[1.05] max-w-2xl">
            Des sites pensés{" "}
            <em className="not-italic italic font-light">
              pour{" "}
              <span className="text-aurora">développer votre activité.</span>
            </em>
          </h2>
          <p
            data-testid="portfolio-mini-banner"
            className="mt-4 text-sm text-[#0F172A]/60 flex items-center gap-2"
          >
            <span
              className="text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
              }}
            >
              ✦
            </span>
            Sélection de projets clients &amp; études de cas
          </p>
        </div>
      </div>

      {/* Horizontal scroller — extends edge-to-edge, slow auto-scroll */}
      <div
        ref={scrollerRef}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resume}
        onFocus={pause}
        onBlur={resume}
        className="overflow-x-auto overflow-y-hidden flex gap-6 md:gap-8 px-6 md:px-10 pb-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {loopProjects.map((p, i) => {
          const Wrapper = p.href ? "a" : "article";
          const wrapperProps = p.href
            ? {
                href: p.href,
                target: "_blank",
                rel: "noopener noreferrer",
              }
            : {};
          return (
            <Wrapper
              key={`${p.name}-${i}`}
              data-testid={`portfolio-item-${i + 1}`}
              data-portfolio-card
              className="group shrink-0 w-[82%] sm:w-[58%] md:w-[42%] lg:w-[31%] rounded-3xl bg-white shadow-tactile overflow-hidden hover-lift block"
              {...wrapperProps}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={`${p.name} — ${p.industry}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 right-4 flex flex-col items-start gap-2 md:flex-row md:justify-between md:items-start pointer-events-none">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0B1027] font-bold max-w-full">
                    <span className="truncate">{p.tag}</span>
                  </span>
                  {p.href && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0B1027]/85 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white font-bold shrink-0">
                      Site en ligne
                    </span>
                  )}
                </div>
                <span className="absolute bottom-4 right-4 inline-flex items-center justify-center h-10 w-10 rounded-full btn-aurora opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={18} />
                </span>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight text-[#0B1027] font-medium">
                      {p.name}
                    </h3>
                    <p className="text-xs md:text-sm text-[#0F172A]/60 mt-1">
                      {p.industry}
                    </p>
                  </div>
                  <span className="text-xs text-[#0F172A]/50 mt-1 shrink-0">
                    {p.year}
                  </span>
                </div>
                <p className="mt-4 font-display text-base md:text-lg italic text-[#0F172A]/80 leading-snug">
                  « {p.quote} »
                </p>
              </div>
            </Wrapper>
          );
        })}
      </div>

      {/* Dots below the scroller — centered */}
      <div className="mt-8 flex items-center justify-center gap-2.5" data-testid="portfolio-dots">
        {projects.map((p, i) => {
          const active = i === activeIndex;
          return (
            <button
              key={p.name}
              type="button"
              onClick={() => scrollToIndex(i)}
              aria-label={`Aller au projet ${p.name}`}
              aria-current={active ? "true" : undefined}
              data-testid={`portfolio-dot-${i}`}
              className="group relative flex items-center justify-center h-8 w-8 rounded-full transition-transform hover:scale-110"
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  active
                    ? "h-2.5 w-8"
                    : "h-2 w-2 bg-[#0B1027]/25 group-hover:bg-[#0B1027]/45"
                }`}
                style={
                  active
                    ? {
                        background:
                          "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
                      }
                    : undefined
                }
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}
